import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { ResultsVM } from '../../results-vm';
import { PlatformService } from '../../../services/platform.service';
import { hasDailyLoop } from '../../../services/daily-loop';
import { AppDownloadCardComponent } from '../../../shared/components/app-download/app-download-card.component';

/** paid-intro — "your full picture": greeting + domain summary grid + mobile guided journey. */
@Component({
  selector: 'app-results-intro',
  standalone: true,
  imports: [CommonModule, TranslatePipe, AppDownloadCardComponent],
  templateUrl: './intro.component.html',
})
export class IntroComponent {
  @Input() vm!: ResultsVM;
  /** Surface split B4 (Danna 25.09): the routine lives in the app. On the WEB
   *  the "Your tools" card/row is not offered; the app-download card takes its
   *  place. The app keeps it exactly as it was. */
  readonly isNative = inject(PlatformService).isNative;
  readonly dailyLoop = hasDailyLoop();
  get domains() {
    const all = this.vm?.domains ?? [];
    return this.dailyLoop ? all : all.filter((d) => d.id !== 'routine');
  }
  /** the legend goes right before Your tools when that card is shown (app), else after the result cards */
  get hasRoutine(): boolean {
    return this.domains.some((d) => d.id === 'routine');
  }
  /** emits the target panelId when a card / journey step is tapped */
  @Output() goPanel = new EventEmitter<string>();
  /** emits when the doctor-report card / journey step is tapped (routes away, not a panel) */
  @Output() goDoctor = new EventEmitter<void>();
  /** emits when a LOCKED card (doctor / Option-A routine) is tapped → paywall modal */
  @Output() goPaywall = new EventEmitter<void>();

  /** neutral pill colours when a module wasn't completed */
  readonly emptyTag = { bg: '#f3f1ee', color: '#6d6d6d' };
  /** pill for a scoreless-but-completed module (e.g. SENSE): saved, no score/band */
  readonly savedTag = { bg: '#e9f6ee', color: '#2f9e6b' };

  // --- Paywall v2 locks (locked-flow spec §1) ---
  /** doctor summary (paid-4) is locked for any non-entitled account (both A & B).
   *  Entitled = paid OR admin OR admin-granted credit, so admins see it open. */
  get doctorLocked(): boolean {
    return !this.vm?.isEntitled;
  }

  /** routine (paid-0) is locked only in Option A (tools sold); free in B. */
  get routineLocked(): boolean {
    return !this.vm?.isEntitled && !!this.vm?.toolsLocked;
  }
}
